import {ServiceBase} from "../ServiceBase";
import {IPlayerOfferingsService} from "./player-offerings.service.interface";
import {action, observable} from "mobx";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {Check} from "@sparkware/app-utils";
import {BalanceType} from "../../../common/const/wallet_balance_type";
import { CmsBannerId } from "../cms/cms-banners/cm-banner-id/cms-banner-id";
import { LinkToOfferConfiguration } from "../configurations/config-data-sections/link-to-offer.configuration";
import { WelcomeOffer } from "./player-offerings/offers/welcome-offer";
import { WheelOfFortuneOffer } from "./player-offerings/offers/wheel-of-fortune-offer";
import React from "react";

const ANIMATE_WHEEL_BALANCE_THRESHOLD = 500; // 500 = 5.00(Dollars/Euros/GBPs)

export interface IOffer {
    shouldBeVisible(): boolean;
    init();
    render(): React.ReactNode;
    shoulUseLinkToOfferAnimation(): boolean;
    readonly id: string;
    readonly bannerId: CmsBannerId;
    readonly analyticsCategory: string;
}


export class PlayerOfferingsService extends ServiceBase implements IPlayerOfferingsService {
    @observable.ref enableWheelOfFortuneAnimation: boolean = false;

    @observable.ref
    private _hasWheelOfFortunePromotion: boolean = false;

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this._subscribe();
    }

    get isWheelOfFortuneEnabled() {
        const isEnabledFromConfig = this.services.configuration.servicesConfigs.PlayerOfferings.isWheelOfFortuneEnabled;
        const hasWheelOfFortuneConfigSection = !Check.isNullOrUndefined(this.services.configuration.data.wheelOfFortune);
        return isEnabledFromConfig && hasWheelOfFortuneConfigSection;
    }

    @action
    private _setWOFEnableAnimation(enableAnimation: boolean) {
        this.enableWheelOfFortuneAnimation = enableAnimation;
    }

    
    @action
    private _setHasWheelOfFortunePromotion(hasWheelOfFortunePromotion: boolean) {
        this._hasWheelOfFortunePromotion = hasWheelOfFortunePromotion;
    }

    getOffers(config: LinkToOfferConfiguration): IOffer[] {
        const offers: IOffer[] = [];
        // order of this insertions matter!
        if (this.isWheelOfFortuneEnabled && this.services.configuration.data.wheelOfFortune) {
            offers.push(new WheelOfFortuneOffer(this.services, this.services.configuration.data.wheelOfFortune));
        }
        if (config?.welcomeBonus) {
            offers.push(new WelcomeOffer(this.services, config.welcomeBonus));
        }

        return offers;
    }

    private _subscribe() {
        this.reaction(() => ({
                isAuthorized: this.services.user.isAuthorized,
                userBalance: this.services.walletProxy.plainNumberBankroll[BalanceType.BOTH]
            }),
            ({isAuthorized, userBalance}) => {
                if (this.isWheelOfFortuneEnabled) {
                    if (isAuthorized && Check.isNumber(userBalance) && userBalance < ANIMATE_WHEEL_BALANCE_THRESHOLD) {
                        this._setWOFEnableAnimation(true);
                    } else if(!isAuthorized){
                        this._setWOFEnableAnimation(false);
                    }
                }
            },
            {fireImmediately: true}
        );

        this.reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                if (!isAuthorized) {
                    this._hasWheelOfFortunePromotion = false;
                } else {
                    this._updateWheelOfFortuneAvailability();
                }
            }, { fireImmediately: true }
        );

        this.services.sdk.events.onPromotionsChanged(data => {
            if (this.services.user.isAuthorized &&
                this.services.user.cid.toString() === data.Cid.toString() && 
                Check.isBoolean(data.IsPromotionDisplayedInClient)) {
                //this data.IsPromotionDisplayedInClient info seems to be wrong
                //this._setHasWheelOfFortunePromotion(data.IsPromotionDisplayedInClient);
                //so use API instead
                this._updateWheelOfFortuneAvailability();
            }
        });
    }

    private async _fetchUserHasWheelOfFortune() {
        try {
            const promoCodes = await this.services.api.bonusManagerController.getEligiblePromotionCodes();
            return Check.isArray(promoCodes) && promoCodes.length >= 1;
        }
        catch (err) {
            // we might receive an error like LimitReached (122), which anyway means that we don't have any promotion codes
            return false;
        }
    }

    private _updateWheelOfFortuneAvailability() {
        if (this.isWheelOfFortuneEnabled) {
            this._fetchUserHasWheelOfFortune().then(hasWheelOfFortune => {
                //make sure user is still authenticated when we set this response
                if (this.services.user.isAuthorized) {
                    this._setHasWheelOfFortunePromotion(hasWheelOfFortune);
                }
            });
        }
    }

    get hasWheelOfFortunePromotion(): boolean {
        return this._hasWheelOfFortunePromotion;
    }
}
